import { useEffect } from 'react';

import PullToRefresh from 'pulltorefreshjs';

import { isNativeApp } from '@helpers/isNativeApp';

// Matches the bottom-nav active/selected tint (`$cerulean-60` in the SCSS
// palette) so the refresh spinner feels consistent with the app's chrome.
const SPINNER_ACTIVE_COLOR = '#0089ab';

// Pull-distance tuning (px). Shared between the library config and the
// progress-ring updater so they stay in sync.
const DIST_THRESHOLD = 70; // pull distance required before release triggers refresh
const DIST_MAX = 110; // max draggable distance
const DIST_RELOAD = 72; // resting height while refreshing

// Geometry of the SVG progress ring. The circumference drives the
// stroke-dash math that fills the arc as the user pulls.
const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Determinate progress ring shown WHILE PULLING — a light gray track with a
 * cerulean arc whose length is driven by the pull distance (see the rAF loop
 * in the hook, which sets `stroke-dashoffset`). Starts empty
 * (`stroke-dashoffset === circumference`).
 */
const progressRingSvg = (): string => `
<svg class="ptr-spinner" width="26" height="26" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <circle cx="25" cy="25" r="${RING_RADIUS}" fill="none" stroke="#e5e7eb" stroke-width="5" />
  <circle class="ptr-progress-bar" cx="25" cy="25" r="${RING_RADIUS}" fill="none" stroke="${SPINNER_ACTIVE_COLOR}" stroke-width="5" stroke-linecap="round" transform="rotate(-90 25 25)" stroke-dasharray="${RING_CIRCUMFERENCE}" stroke-dashoffset="${RING_CIRCUMFERENCE}" />
</svg>
`;

/**
 * Indeterminate spinner shown WHILE REFRESHING — a cerulean quarter-arc that
 * spins continuously via the `ptr-spinner--spin` animation.
 */
const spinnerSvg = (): string => `
<svg class="ptr-spinner ptr-spinner--spin" width="26" height="26" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <circle cx="25" cy="25" r="${RING_RADIUS}" fill="none" stroke="#e5e7eb" stroke-width="5" />
  <path fill="none" stroke="${SPINNER_ACTIVE_COLOR}" stroke-width="5" stroke-linecap="round" d="M25 5 a${RING_RADIUS} ${RING_RADIUS} 0 0 1 ${RING_RADIUS} ${RING_RADIUS}" />
</svg>
`;

/**
 * Style override for the pull-to-refresh indicator.
 *
 * The library injects the indicator as the body's first child with static
 * positioning, no z-index, and a faint text colour — so the app's sticky
 * header (z-index 251) paints over it. We make it `position: fixed` with a
 * high z-index and a solid white background so it floats above the app chrome,
 * hide the default text label for a clean spinner-only look, and define the
 * keyframes that spin the circular icon while refreshing.
 *
 * `__PREFIX__` is replaced by the library with the class prefix (`ptr--`).
 */
const PTR_STYLES = `
.__PREFIX__ptr {
  pointer-events: none;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  background: transparent;
  height: 0;
  transition: height 0.3s, min-height 0.3s;
  text-align: center;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  align-content: stretch;
}
.__PREFIX__box { padding: 12px; flex-basis: 100%; }
.__PREFIX__pull { transition: none; }
.__PREFIX__text { display: none; }
.__PREFIX__icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: #ffffff;
  border-radius: 9999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}
.__PREFIX__top { touch-action: pan-x pan-down pinch-zoom; }
.ptr-spinner { display: inline-block; transform-origin: 50% 50%; }
.ptr-spinner--spin { animation: ptr-spin 0.7s linear infinite; }
@keyframes ptr-spin { 100% { transform: rotate(360deg); } }
`;

/**
 * Adds native-style pull-to-refresh ONLY when the web app is running inside
 * the SparkPlug native shell (iOS/Android WebView). In a regular browser the
 * gesture is left untouched so the desktop/mobile-web experience is unchanged.
 *
 * Why web-side instead of the WebView's `pullToRefreshEnabled` prop:
 * that prop is iOS-only in react-native-webview, so a single web-side
 * implementation gives us identical behaviour on both platforms without
 * patching the native library.
 *
 * The gesture only fires when the document is scrolled to the top
 * (`shouldPullToRefresh`), so it won't hijack scrolling inside the page. The
 * web app header is `position: sticky`, meaning the document body is the
 * scroll container — which is exactly what `window.scrollY === 0` checks.
 */
export const usePullToRefresh = (): void => {
    useEffect(() => {
        if (!isNativeApp()) {
            return;
        }

        const instance = PullToRefresh.init({
            mainElement: 'body',
            // Pull distance (px) required before a release triggers a refresh.
            distThreshold: DIST_THRESHOLD,
            // Max distance (px) the indicator can be dragged. Raised above the
            // default 80 so the spinner chip pulls down comfortably lower
            // instead of hugging the top edge.
            distMax: DIST_MAX,
            // Resting height while refreshing. Must be tall enough to fit the
            // spinner chip + box padding, otherwise the chip's top gets clipped
            // by the bar's `overflow: hidden` (showing only ~75% of it).
            distReload: DIST_RELOAD,
            // A hard reload is the most reliable way to refresh "any page"
            // regardless of which route/query state it holds.
            onRefresh: () => window.location.reload(),
            // Only trigger when the page is at the very top, so mid-page pulls
            // and inner scroll containers (drawers, modals) aren't affected.
            shouldPullToRefresh: () => window.scrollY === 0,
            // Spinner-only look — the text label is hidden via CSS, so the
            // instruction strings are intentionally blank.
            instructionsPullToRefresh: '',
            instructionsReleaseToRefresh: '',
            instructionsRefreshing: '',
            // Determinate progress ring while pulling; indeterminate spinner
            // once the refresh kicks off.
            iconArrow: progressRingSvg(),
            iconRefreshing: spinnerSvg(),
            getStyles: () => PTR_STYLES,
        });

        // Drive the progress ring from the live pull distance. The library
        // sets the indicator's `min-height` to the (resisted) pull distance on
        // every move; we map that to the arc's `stroke-dashoffset` so the ring
        // fills as the user drags and reaches full right at DIST_THRESHOLD.
        //
        // Read the *computed* min-height (not the inline value) so the arc
        // also animates smoothly back to empty when a pull is released without
        // triggering a refresh (the library transitions min-height back to 0).
        let rafId = 0;
        let stopTimeoutId = 0;

        const updateProgressRing = () => {
            const indicator = document.querySelector('.ptr--ptr');
            const bar = indicator?.querySelector('.ptr-progress-bar');
            if (indicator && bar) {
                const height = parseFloat(getComputedStyle(indicator).minHeight) || 0;
                // Fill against the trigger threshold so the ring reaches a full
                // circle exactly when releasing fires the refresh. The exponent
                // eases the early fill so it still feels gradual rather than
                // racing to full.
                const ratio = Math.max(0, Math.min(1, height / DIST_THRESHOLD));
                const progress = Math.pow(ratio, 1.6);
                bar.setAttribute(
                    'stroke-dashoffset',
                    String(RING_CIRCUMFERENCE * (1 - progress)),
                );
            }
            rafId = requestAnimationFrame(updateProgressRing);
        };

        const startTracking = () => {
            window.clearTimeout(stopTimeoutId);
            if (!rafId) {
                rafId = requestAnimationFrame(updateProgressRing);
            }
        };

        // Keep the loop alive briefly after release so the retract animation
        // (min-height → 0) is reflected before we stop polling.
        const stopTracking = () => {
            window.clearTimeout(stopTimeoutId);
            stopTimeoutId = window.setTimeout(() => {
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = 0;
                }
            }, 500);
        };

        const startEvents = ['touchstart', 'pointerdown'] as const;
        const endEvents = ['touchend', 'touchcancel', 'pointerup', 'pointercancel'] as const;
        startEvents.forEach((evt) =>
            window.addEventListener(evt, startTracking, { passive: true }),
        );
        endEvents.forEach((evt) =>
            window.addEventListener(evt, stopTracking, { passive: true }),
        );

        return () => {
            startEvents.forEach((evt) => window.removeEventListener(evt, startTracking));
            endEvents.forEach((evt) => window.removeEventListener(evt, stopTracking));
            window.clearTimeout(stopTimeoutId);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            instance.destroy();
        };
    }, []);
};
